import React, { useState, useRef, useEffect } from 'react'
import Dfp from 'react-simple-dfp'
import { useRouter } from 'next/router'

import { FlexDiv, FlexRowDiv, CenteringDiv } from 'components/generic/HomePageComponents'
import DirectorySEO from 'components/SEO/DirectorySEO'
import SimpleErrorPage from 'components/error/SimpleErrorPage'
import DirectoryQuery from 'components/Directories/API/Queries/DirectoryQuery.gql'
import HeroBanner from 'components/Directories/CTAs/HeroBanner'
import KathyCTA from 'components/Directories/CTAs/KathyCTA'
import UpgradeCTA from 'components/Directories/CTAs/UpgradeCTA'
import SearchFacetsContainer from './SearchFacetsContainer'
import ResultsCount from 'components/Directories/ResultsCount'
import Listings from 'components/Directories/Listings'
import Paginator from 'components/Directories/Paginator'
import Breadcrumbs from 'components/Directories/Breadcrumbs'
import DirectoryLoadingScreen from 'components/Directories/DirectoryLoadingScreen'
import { DirectoryWrapper, SpaceAroundDiv, AdContainerDiv } from 'components/Directories/Styled'
import {
  PROVIDER_TYPES,
  KNOWN_ERROR_MESSAGES,
  DEFAULT_SEARCH_INFORMATION_STATE,
} from 'components/Directories/Constants'
import ReasonToHireMeCTA from 'components/Directories/CTAs/ReasonToHireMe'
import { useQuery } from '@apollo/client'

const DirectoriesContainer = (props) => {
  const previousDirectoryQueryData = useRef(null)
  const router = useRouter()

  const resetFilters = () => {
    setPostalCode(DEFAULT_SEARCH_INFORMATION_STATE.postalCode)
    setRadius(DEFAULT_SEARCH_INFORMATION_STATE.radius)
    setSelectedLanguageId(DEFAULT_SEARCH_INFORMATION_STATE.selectedLanguageId)
    setInvalidPostalCode(DEFAULT_SEARCH_INFORMATION_STATE.invalidPostalCode)
  }

  const setInvalidPostalCodeIfRequired = (value) => {
    if (invalidPostalCode !== value) {
      setInvalidPostalCode(value)
    }
  }

  // reset postal code and other filters when city/state/province/country changed
  useEffect(() => {
    resetFilters()
  }, [router.query.locations])

  const [postalCode, setPostalCode] = useState(DEFAULT_SEARCH_INFORMATION_STATE.postalCode)
  const [radius, setRadius] = useState(DEFAULT_SEARCH_INFORMATION_STATE.radius)
  const [selectedLanguageId, setSelectedLanguageId] = useState(
    DEFAULT_SEARCH_INFORMATION_STATE.selectedLanguageId
  )
  const [invalidPostalCode, setInvalidPostalCode] = useState(
    DEFAULT_SEARCH_INFORMATION_STATE.invalidPostalCode
  )

  const {
    directoryLabel,
    directoryResultsType,
    directoryProvider,
    page,
    location,
    facets,
    directoriesCanonicalUrl,
    keyword,
    directoryLinkInformation,
    viewer,
    loggedIn,
  } = props

  const queryVariables = {
    params: {
      page: page,
      type: directoryResultsType,
      selectedFacets: facets,
      location: {
        country: location && 'country' in location ? location.country : null,
        state: location && 'state' in location ? location.state : null,
        city: location && 'city' in location ? location.city : null,
        neighborhood: location && 'neighborhood' in location ? location.neighborhood : null,
        postalCode: postalCode,
        range: radius,
      },
      search: keyword || null,
      selectedLanguageId: selectedLanguageId || null,
      selectedProviderTypes: (directoryProvider && directoryProvider.facetKeyword) || null,
    },
  }
  const { loading, error, data: directoryQueryData } = useQuery(DirectoryQuery, {
    variables: queryVariables,
  })

  if (error) {
    if (error.message.includes(KNOWN_ERROR_MESSAGES.invalid_postal_code)) {
      setInvalidPostalCodeIfRequired(true)
    } else {
      setInvalidPostalCodeIfRequired(false)
      return <SimpleErrorPage error={error} />
    }
  } else {
    setInvalidPostalCodeIfRequired(false)
  }

  previousDirectoryQueryData.current = directoryQueryData || previousDirectoryQueryData.current
  const data = previousDirectoryQueryData.current

  const directory = data?.site?.directory
  if (!directory) {
    return <DirectoryLoadingScreen />
  }
  const nearestLocationData = directory.nearestLocation

  const numOfResults = directory.count
  const listings = directory.listings
  const siteDirectoryResultsTypes = directory.siteKeywords
  const facetGroups = directory.facetGroups
  const providers = facetGroups && facetGroups.find((facetGroup) => facetGroup.slug === 'provider')
  const providerRadioFacets =
    providers &&
    providers.facets &&
    providers.facets.length > 0 &&
    providers.facets.filter(
      (facet) =>
        facet.count > 0 &&
        (facet.slug === PROVIDER_TYPES.BUSINESS || facet.slug === PROVIDER_TYPES.INDIVIDUALS)
    )
  const providerMultiSelectFacets =
    providers &&
    providers.facets &&
    providers.facets.length > 0 &&
    providers.facets.filter(
      (facet) => facet.count > 0 && !(providerRadioFacets && providerRadioFacets.includes(facet))
    )
  const allOtherFacets =
    facetGroups && facetGroups.filter((facetGroup) => facetGroup.slug !== 'provider')

  const headerCta = directory.headerCta
  const heroBannerTitle = headerCta?.title
  const heroBannerText = headerCta?.text
  const heroBannerCtaButtonLink = headerCta?.button.link
  const heroBannerCtaButtonText = headerCta?.button.text

  const invertedCta = directory.invertedCta
  const postProfileCtaText = invertedCta?.text
  const postProfileCtaButtonLink = invertedCta?.button.link
  const postProfileCtaButtonText = invertedCta?.button.text

  const kathyCta = directory.kathyCta
  const kathyCtaTitle = kathyCta?.title
  const kathyCtaImage = kathyCta?.image
  const kathyCtaText = kathyCta?.text
  const kathyCtaButtonLink = kathyCta?.button.link
  const kathyCtaButtonText = kathyCta?.button.text

  const locationOptions = directory.locationDropdowns
  const breadCrumbs = directory.breadcrumbs
  const metaTagsData = directory.metaTags
  const languages = directory.languages

  const profileEligibleForUpgrade = !!viewer?.profileEligibleForUpgrade
  const isSubscribed = !!viewer?.is_subscribed
  const hasListing = !!viewer?.has_active_listing

  const user = viewer?.user
  const userListings = user?.listing
  const editHireReasonsPath = userListings?.editHireReasonsPath
  const hireReasons = userListings?.hireReasons ? userListings?.hireReasons : []
  let hasReasons = null
  if (viewer && Object.keys(viewer).length > 0) {
    hasReasons = hireReasons && hireReasons.length > 0
  }
  const approvedProfile = !!userListings?.isApproved

  return (
    <>
      <DirectorySEO metaTagsData={metaTagsData} directoriesCanonicalUrl={directoriesCanonicalUrl} />
      <DirectoryWrapper>
        <AdContainerDiv display={{ _: 'none', tablet: 'flex' }}>
          <Dfp
            display='flex'
            id='div-gpt-ad-1584984399509-0'
            adUnitPath='/23314643/directory-leaderboard'
            adSize={[728, 90]}
            adCollapse={true}
            adStyle={{ width: '728px', height: '90px', margin: '8px 0' }}
          />
        </AdContainerDiv>
        <AdContainerDiv display={{ _: 'flex', tablet: 'none' }}>
          <Dfp
            id='div-gpt-ad-1584984399509-1'
            adUnitPath='/23314643/directory-leaderboard'
            adSize={[320, 50]}
            adCollapse={true}
          />
        </AdContainerDiv>
        {hasListing === false && (
          <HeroBanner
            title={heroBannerTitle}
            text={heroBannerText}
            ctaButtonLink={heroBannerCtaButtonLink}
            ctaButtonText={heroBannerCtaButtonText}
          />
        )}
        <FlexRowDiv backgroundColor='#FBFBFB'>
          <CenteringDiv maxWidth='1259px' mb='48px' mr='16px' ml='16px' width='100%'>
            <FlexDiv
              justify-content='space-around'
              flexDirection='row'
              pb='16px'
              alignSelf='flex-start'
            >
              <Breadcrumbs
                breadCrumbsData={breadCrumbs}
                directoryLinkInformation={directoryLinkInformation}
              />
            </FlexDiv>
            <SpaceAroundDiv flexDirection={{ _: 'column', tablet: 'row' }}>
              <SearchFacetsContainer
                siteDirectoryResultsTypes={siteDirectoryResultsTypes}
                directoryProvider={directoryProvider}
                directoryResultsType={directoryResultsType}
                providerFacetName={providers && providers.name}
                providerRadioFacets={providerRadioFacets}
                providerMultiSelectFacets={providerMultiSelectFacets}
                otherFacets={allOtherFacets}
                selectedFacets={facets}
                directoryLinkInformation={directoryLinkInformation}
                keyword={keyword}
                locationOptions={locationOptions}
                postalCodeUpdate={setPostalCode}
                postalRadiusUpdate={setRadius}
                invalidPostalCode={invalidPostalCode}
                languages={languages}
                selectedLanguageId={selectedLanguageId}
                setLanguage={setSelectedLanguageId}
                resetFilters={resetFilters}
                nearestLocation={nearestLocationData}
              />
              <FlexDiv flexDirection='column'>
                {loggedIn === true && hasListing === true && approvedProfile === true && (
                  <ReasonToHireMeCTA
                    isLoading={loading}
                    hasReasons={hasReasons}
                    reasonsEditLink={editHireReasonsPath}
                  />
                )}
                <UpgradeCTA
                  profileEligibleForUpgrade={profileEligibleForUpgrade}
                  isSubscribed={isSubscribed}
                  hasListing={hasListing}
                  isLoading={loading}
                />
                <ResultsCount
                  numOfResults={numOfResults}
                  city={location && location.city}
                  directoryLabel={directoryLabel}
                  isLoading={loading}
                />
                <Listings
                  listings={listings}
                  postProfileCtaText={postProfileCtaText}
                  postProfileCtaButtonLink={postProfileCtaButtonLink}
                  postProfileCtaButtonText={postProfileCtaButtonText}
                  hasListing={hasListing}
                  isLoading={loading}
                />
                <Paginator
                  directoryLinkInformation={directoryLinkInformation}
                  page={page}
                  count={numOfResults}
                  perPage={listings ? listings.count : 0}
                />
                <KathyCTA
                  kathyCtaTitle={kathyCtaTitle}
                  kathyCtaImage={kathyCtaImage}
                  kathyCtaText={kathyCtaText}
                  kathyCtaButtonLink={kathyCtaButtonLink}
                  kathyCtaButtonText={kathyCtaButtonText}
                  hasListing={hasListing}
                  isLoading={loading}
                />
              </FlexDiv>
            </SpaceAroundDiv>
          </CenteringDiv>
        </FlexRowDiv>
      </DirectoryWrapper>
    </>
  )
}

export default DirectoriesContainer
